import { useEffect, useRef, useState } from 'react';

/**
 * Measures a flex row of `n` equal bar cells (gap included) and returns each
 * bar's centre as a percentage of the row width. Overlay elements — the hover
 * crosshair, year dividers and tick dots — use these so they line up with the
 * real bars instead of the naive `(i + 0.5) / n` approximation, which drifts
 * once the flex gap is applied.
 *
 * Re-measures on resize so the positions stay correct at every breakpoint.
 */
export default function useBarAxis(n: number) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [centers, setCenters] = useState<number[]>([]);

  useEffect(() => {
    const el = ref.current;
    if (!el || n <= 0) return undefined;

    const measure = () => {
      const width = el.clientWidth;
      if (width <= 0) return;
      const parsed = parseFloat(getComputedStyle(el).columnGap || '0');
      const gap = Number.isFinite(parsed) ? parsed : 0;
      const cell = (width - gap * (n - 1)) / n;
      if (cell <= 0) return;
      const next = Array.from({ length: n }, (_, i) => ((i * (cell + gap) + cell / 2) / width) * 100);
      setCenters((prev) =>
        prev.length === next.length && prev.every((value, i) => Math.abs(value - next[i]) < 0.05)
          ? prev
          : next,
      );
    };

    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [n]);

  return { ref, centers };
}